import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/bi/AppShell";
import { Loading } from "@/components/bi/Loading";
import { EmptyState } from "@/components/bi/EmptyState";
import { StatusBadge } from "@/components/bi/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { PortabilityDialog } from "@/components/bi/PortabilityDialog";
import {
  Building2, FolderKanban, LayoutDashboard, ArrowRight, Rocket, Upload, Star, Plus,
} from "lucide-react";

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const m = Math.round(Math.max(0, Date.now() - t) / 60000);
  if (m < 60) return `${Math.max(1, m)}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d < 7 ? `${d}d ago` : new Date(iso).toLocaleDateString();
}

const Stat = ({ label, value, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-4 py-3">
    <div className="font-display text-2xl text-[color:var(--ink)] tabular-nums">{value}</div>
    <div className="text-[11px] uppercase tracking-wide text-[color:var(--ink-3)]">{label}</div>
  </div>
);

// Item 25: Tenant -> Site/Client dashboard home hierarchy.
export default function TenantHome() {
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/sites/tenant-home");
      setState(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load home");
      setState({ sites: [], recent: [], totals: {} });
    }
  };
  useEffect(() => { load(); }, []);

  if (state === null) return <AppShell title="Home"><Loading label="Loading your workspace" /></AppShell>;
  const { tenant, sites = [], recent = [], totals = {}, is_admin: isAdmin } = state;

  const actions = (
    <div className="flex items-center gap-2">
      {isAdmin && (
        <Button data-testid="tenant-home-import-btn" variant="outline" onClick={() => setImportOpen(true)} className="bg-[color:var(--surface)]">
          <Upload className="w-4 h-4 mr-1" />Import dashboard
        </Button>
      )}
      <Button data-testid="tenant-home-all-dashboards" variant="outline" onClick={() => nav("/")} className="bg-[color:var(--surface)]">
        All dashboards <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  );

  return (
    <AppShell title="Home" actions={actions}>
      <PortabilityDialog open={importOpen} onOpenChange={setImportOpen} mode="import"
        onDone={() => { setImportOpen(false); load(); }} />
      <div className="px-5 md:px-8 py-6 max-w-6xl mx-auto space-y-7">
        <div data-testid="tenant-home-hero" className="glass-card p-6">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[color:var(--ink-3)]">
            <Building2 className="w-3.5 h-3.5" />Organisation
          </div>
          <h1 data-testid="tenant-home-name" className="font-display text-3xl text-[color:var(--ink)] mt-1.5">{tenant?.name || "My organisation"}</h1>
          <p className="text-sm text-[color:var(--ink-3)] mt-2 max-w-2xl">
            Every client site in this organisation, with its own dashboard home. Open a site to see its featured
            and recent dashboards.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5 max-w-xl">
            <Stat testid="tenant-stat-sites" label="Sites / clients" value={totals.sites ?? sites.length} />
            <Stat testid="tenant-stat-dashboards" label="Dashboards" value={totals.dashboards ?? 0} />
            <Stat testid="tenant-stat-published" label="Published" value={totals.published ?? 0} />
          </div>
        </div>

        <section data-testid="tenant-home-sites">
          <h2 className="text-base md:text-lg font-display text-[color:var(--ink)] mb-3 flex items-center gap-2">
            <FolderKanban className="w-4 h-4 text-[color:var(--brand-ocean)]" />Sites &amp; clients
          </h2>
          {sites.length === 0 ? (
            <EmptyState testid="tenant-home-no-sites" icon={FolderKanban} title="No sites yet"
              description="Create a site to group dashboards per client or business unit."
              action={<Button data-testid="tenant-home-create-site" onClick={() => nav("/")}><Plus className="w-4 h-4 mr-1" />Manage sites</Button>} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {sites.map((s) => (
                <div key={s.id} data-testid={`tenant-site-card-${s.id}`} onClick={() => nav(`/sites/${s.id}`)}
                  className="glass-card p-5 cursor-pointer hover:shadow-[0_22px_70px_rgba(15,23,42,0.14)] transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-xl hero-gradient flex items-center justify-center border border-[color:var(--glass-border)]">
                      <FolderKanban className="w-5 h-5 text-[color:var(--brand-ocean)]" />
                    </div>
                    {s.is_default && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[color:var(--surface-2)] text-[color:var(--ink-3)]">Default</span>}
                  </div>
                  <h3 className="font-display text-lg text-[color:var(--ink)] mt-4 truncate">{s.name}</h3>
                  <p className="text-sm text-[color:var(--ink-3)] mt-1 line-clamp-2 min-h-[40px]">{s.description || s.home_intro || "No description"}</p>
                  <div className="flex items-center gap-3 mt-3 text-[11px] text-[color:var(--ink-3)]">
                    <span className="flex items-center gap-1"><LayoutDashboard className="w-3.5 h-3.5" />{s.dashboard_count} dashboards</span>
                    <span className="flex items-center gap-1 text-[color:var(--brand-mint)]"><Rocket className="w-3.5 h-3.5" />{s.published_count} live</span>
                    <span className="ml-auto">{relTime(s.last_updated)}</span>
                  </div>
                  {(s.featured || []).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[color:var(--glass-border)] space-y-1">
                      {(s.featured || []).map((f) => (
                        <div key={f.id} className="flex items-center gap-1.5 text-xs text-[color:var(--ink-2)] truncate">
                          <Star className="w-3 h-3 text-[color:var(--brand-mint)] shrink-0" />{f.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {recent.length > 0 && (
          <section data-testid="tenant-home-recent">
            <h2 className="text-base md:text-lg font-display text-[color:var(--ink)] mb-3">Recently updated</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {recent.map((d) => (
                <div key={d.id} data-testid={`tenant-recent-card-${d.id}`} onClick={() => nav(`/builder/${d.id}`)}
                  className="glass-card p-4 cursor-pointer hover:shadow-[0_18px_50px_rgba(15,23,42,0.12)] transition-shadow">
                  <div className="flex items-center justify-between">
                    <LayoutDashboard className="w-4 h-4 text-[color:var(--brand-ocean)]" />
                    <StatusBadge status={d.status} />
                  </div>
                  <div className="font-medium text-sm text-[color:var(--ink)] mt-3 truncate">{d.name}</div>
                  <div className="text-[11px] text-[color:var(--ink-3)] mt-1">{d.widget_count} widgets · {relTime(d.updated_at)}</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </AppShell>
  );
}
